/**
 * Shared formatting helpers for comment + activity feeds (Projects, Requests, …).
 * Previously duplicated inside the per-domain feed components.
 */

import { intlLocale } from "@/i18n/intl-locale";
import type { Locale } from "@/i18n/types";

/** What the time helpers need from `useTranslation()`: the reader's words and
 *  the reader's date format, passed in rather than read from a global. */
export interface TimeCopy {
  t: (key: string, values?: Record<string, unknown>) => string;
  locale: Locale;
}

/** "Just now", "N minutes ago" or "N hours ago" within the last day, else null. */
function withinADay(diffMs: number, { t }: TimeCopy): string | null {
  const diffH = diffMs / (1000 * 60 * 60);
  if (diffH < 1) {
    const mins = Math.round(diffMs / 60000);
    return mins <= 1
      ? t("activity.time.just_now")
      : t("activity.time.minutes_ago", { count: mins });
  }
  if (diffH < 24) return t("activity.time.hours_ago", { count: Math.floor(diffH) });
  return null;
}

/** Relative time used by comment rows ("Just now", "3 hours ago", "Yesterday"),
 *  in the reader's language (NG-2614). */
export function formatRelativeTime(iso: string, copy: TimeCopy): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const recent = withinADay(diffMs, copy);
  if (recent) return recent;
  if (diffMs / (1000 * 60 * 60) < 48) return copy.t("activity.time.yesterday");
  return date.toLocaleDateString(intlLocale(copy.locale), {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "3 days ago", "5 weeks ago", "yesterday", "in 2 days": a day count that keeps
 *  counting past 48 hours, where `formatRelativeTime` falls back to a date. For copy
 *  that already prints the date beside it (NG-969). Days under two weeks, else weeks. */
export function formatRelativeDays(iso: string, locale: Locale, now: number = Date.now()): string {
  const days = Math.round((new Date(iso).getTime() - now) / (1000 * 60 * 60 * 24));
  const formatter = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: "auto" });
  return Math.abs(days) < 14
    ? formatter.format(days, "day")
    : formatter.format(Math.round(days / 7), "week");
}

/** Relative time used by the activity timeline (adds the time of day), in the
 *  reader's language (NG-2614). */
export function formatActivityTime(iso: string, copy: TimeCopy): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const recent = withinADay(diffMs, copy);
  if (recent) return recent;
  const tag = intlLocale(copy.locale);
  const timeStr = date.toLocaleTimeString(tag, { hour: "numeric", minute: "2-digit" });
  if (diffMs / (1000 * 60 * 60) < 48)
    return copy.t("activity.time.yesterday_at", { time: timeStr });
  const dateStr = date.toLocaleDateString(tag, { month: "short", day: "numeric" });
  return `${dateStr} · ${timeStr}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

// Design-token gradients for user avatars (from colors_and_type.css).
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, var(--color-accent2-500), var(--color-accent4-500))",
  "linear-gradient(135deg, var(--color-accent3-500), var(--color-error-600))",
  "linear-gradient(135deg, var(--color-accent1-500), var(--color-error-600))",
  "linear-gradient(135deg, var(--color-primary-500), var(--color-primary-950))",
];

export function getAvatarGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length]!;
}

// ── Activity verb + field labels ──────────────────────────────────────────────
// Activity rows store machine verbs ("field_changed") and snake_case field
// slugs ("actual_completion_date"). Render human-friendly text instead.

const VERB_LABELS: Record<string, string> = {
  created: "created this",
  field_changed: "changed",
  // A person's change on a project's Automations tab (NG-3688); its field is the
  // rule's name as written, which `activityFieldText` shows quoted.
  automation_changed: "changed automation",
  // A work item the "Archive closed work items" automation archived, on its
  // project's feed (NG-3891); its field is the item's name, quoted as written.
  work_item_archived: "archived work item",
  state_changed: "changed status",
  status_changed: "changed status",
  commented: "commented",
  linked: "linked",
  unlinked: "unlinked",
  assigned: "assigned",
  unassigned: "unassigned",
  archived: "archived",
  restored: "restored",
  // The verb a restore writes (work items, and a project restore since NG-3531); the
  // UI calls the action Restore.
  unarchived: "restored",
  approval_requested: "requested approval",
  approved: "approved this",
  rejected: "rejected this",
  duplicated: "duplicated this from",
  // A reviewer decided a request's workflow Review step (#3624). Previously fell
  // through humanizeSlug to "review decided" (#4197).
  review_decided: "decided this review",
  // A request's priority pinned by hand, or handed back to the score (NG-3671).
  // Both name their field, and the old -> new chips carry the tier names.
  priority_overridden: "changed priority",
  priority_override_cleared: "reverted priority to auto",
  // SLA activity log
  updated: "updated this SLA",
  activated: "activated this SLA",
  deactivated: "deactivated this SLA",
  condition_added: "added condition",
  condition_removed: "removed condition",
  target_changed: "changed target",
};

const FIELD_LABELS: Record<string, string> = {
  state: "status",
  actual_completion_date: "actual completion date",
  target_completion_date: "target completion date",
  start_date: "start date",
  due_date: "due date",
  required_by: "required by",
  estimated_hours: "estimated effort",
  project_leads: "project leads",
  request_owner: "request owner",
  request_reviewer: "request reviewer",
  requester: "requester",
  // Work-item activity stores the column it diffed, foreign keys included
  // (`work_items/activity_log.py`); the history endpoint names their values
  // (NG-2569), and these name the field the way the work item's own page does.
  state_id: "status",
  stage_id: "stage",
  parent_id: "parent",
  estimate_hours: "estimated effort",
  description_html: "description",
  skills: "qualifications",
  // The Plan work card's own `response_data` keys, as the card labels them.
  // Request rows written since NG-2569 already carry these labels; rows written
  // before it carry the raw key, and read through here.
  plan_project_lead_ids: "project lead",
  plan_delivery_team_ids: "delivery team",
  plan_template_id: "project template",
  plan_start_date: "project start date",
  plan_end_date: "project end date",
  plan_role_hours: "required roles",
  skill_ids: "required qualifications",
};

/** A stored id. It means nothing to a reader, so no feed shows one (NG-2569). */
const OPAQUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a field key is a stored id rather than a name: a custom field with no
 *  label arrives keyed by its UUID, and has no readable form to fall back on. */
export function isOpaqueFieldKey(field: string | null | undefined): boolean {
  return Boolean(field && OPAQUE_ID.test(field.trim()));
}

/**
 * An activity or notification value with every bare id taken out, or `null`
 * when nothing readable is left.
 *
 * The backends name the ids they know about, but a row written before they did
 * still holds the id (no fix rewrites history), and a field nothing resolves
 * keeps what it stored. Either way a reader is better served by no value than by
 * `a0938095-…`. A value is read as a comma-separated list, the shape a feed
 * stores a set of ids in; one with no id in it comes back exactly as it was.
 */
export function readableActivityValue(value: string | null | undefined): string | null {
  // Runtime payloads are not always the declared string (a choice field's
  // notification carries a list): anything else passes through untouched.
  if (typeof value !== "string") return value ?? null;
  if (!value) return null;
  const parts = value.split(",").map((part) => part.trim());
  if (!parts.some((part) => OPAQUE_ID.test(part))) return value;
  const kept = parts.filter((part) => part && !OPAQUE_ID.test(part));
  return kept.length > 0 ? kept.join(", ") : null;
}

function humanizeSlug(slug: string): string {
  return slug.replace(/[_-]+/g, " ").trim().toLowerCase();
}

/** Human label for an activity verb (falls back to a de-slugged form).
 *  Nullish-tolerant: runtime rows can omit the verb entirely.
 *
 *  Pass the row's `field` where the caller has one. It disambiguates `updated`,
 *  a verb SLA has claimed outright in this map ("updated this SLA", alongside
 *  `activated`, `deactivated`, `condition_added` and `target_changed`) while
 *  other feeds write it too. Without the field, a work item's scalar change read
 *  "updated this SLA <field>" — found while building NG-1595.
 *
 *  **Two feeds are affected, because two write a field-ful `updated` AND render
 *  through `ActivityTimeline`**: work items (`work_items/activity_log.py`, the
 *  subject of NG-1595) and programs, whose team-access change
 *  (`core/services/program_activity.py`, `field="team_access"`) read the same
 *  broken sentence. Teams and project workflows also write `updated`, but their
 *  feeds translate verbs themselves and never reach this function.
 *
 *  **INVARIANT: SLA is the only caller-visible feed that writes a field-less
 *  `updated`.** Its row summarises a whole save
 *  (`sla/activity_log.py::record_sla_updated` puts the changed field labels in
 *  `new_value`), while the other two record exactly one field and always carry
 *  it. A new feed writing a field-less `updated` silently inherits the SLA
 *  sentence. The honest fix is one layer down — the work-item feed should write
 *  `field_changed`, which this map already renders as "changed" — or one layer
 *  up, with per-domain wording passed in the way `loadingLabel` already is.
 *
 *  Omitting `field` keeps the SLA reading. */
export function verbLabel(verb: string | null | undefined, field?: string | null): string {
  if (!verb) return "";
  if (verb === "updated" && field) return "changed";
  return VERB_LABELS[verb] ?? humanizeSlug(verb);
}

/**
 * Verbs whose label makes their own field redundant.
 *
 * Rows arrive as (verb, field) and the timeline renders "<verb> <field>", which
 * is right for `field_changed` + `request_owner` -> "changed request owner".
 * But `state_changed` already reads "changed status", so pairing it with its
 * own field (`state` -> "status") rendered **"changed status status"**. Skip
 * the field for these rather than shortening the verb to "changed": the verb
 * has to stand alone when a row carries no field at all.
 *
 * `review_decided` is here for the adjacent reason: it doesn't repeat the word,
 * but "decided this review status" is noise — the status move is already spelled
 * out by the old -> new chips beside it. Its verb likewise has to stand alone,
 * since a decision that changes no status carries no field (#4197).
 */
const STATUS_FIELDS: ReadonlySet<string> = new Set(["state", "status"]);
const PRIORITY_FIELDS: ReadonlySet<string> = new Set(["priority"]);

/** The fields each such verb already names — only those are redundant. A
 *  `state_changed` row pointing at some *other* field is still worth showing.
 *  The priority verbs read "changed priority" / "reverted priority to auto", so
 *  their `priority` field would repeat too (NG-3671). */
const FIELDS_NAMED_BY_VERB: Record<string, ReadonlySet<string>> = {
  state_changed: STATUS_FIELDS,
  status_changed: STATUS_FIELDS,
  review_decided: STATUS_FIELDS,
  priority_overridden: PRIORITY_FIELDS,
  priority_override_cleared: PRIORITY_FIELDS,
};

/**
 * Whether the field should be rendered next to the verb, or would just repeat
 * it. Callers that render "<verb> <field>" should gate the field on this.
 */
export function fieldIsRedundantWithVerb(
  verb: string | null | undefined,
  field: string | null | undefined,
): boolean {
  if (!verb || !field) return false;
  return FIELDS_NAMED_BY_VERB[verb]?.has(field) ?? false;
}

/** Human label for an activity field slug (falls back to a de-slugged form).
 *  Nullish-tolerant: runtime rows can omit the field entirely. A key that is a
 *  stored id has no label to fall back on, so it reads as `""` rather than as
 *  the id; callers leave such a row out (`isOpaqueFieldKey`). */
export function fieldLabel(field: string | null | undefined): string {
  if (!field || isOpaqueFieldKey(field)) return "";
  return FIELD_LABELS[field] ?? humanizeSlug(field);
}

/** Verbs whose field is a name, already human, rather than a field key: an
 *  `automation_changed` row's is the rule's name (backend
 *  `automations/application/project_activity.py`), a `work_item_archived` row's
 *  the archived item's (set by `projectActivityEntry`, NG-3891). */
const NAMED_SUBJECT_VERBS: ReadonlySet<string> = new Set([
  "automation_changed",
  "work_item_archived",
]);

/** The field as a timeline sentence shows it after its verb. A name (see
 *  `NAMED_SUBJECT_VERBS`) is quoted as written rather than de-slugged, which would
 *  lowercase it and split "sub-item". */
export function activityFieldText(
  verb: string | null | undefined,
  field: string | null | undefined,
): string {
  if (verb && NAMED_SUBJECT_VERBS.has(verb) && field) return `'${field}'`;
  return fieldLabel(field);
}

/** Humanize a field key for a standalone heading (target_date -> Target date).
 *
 * Delegates the slug -> words step to `fieldLabel`, so the special cases the
 * activity timeline already knows about (state -> status, skills ->
 * qualifications) read the same way in notifications and the Audit log;
 * `fieldLabel` returns lowercase for mid-sentence use, so it is capitalized
 * here. A value that does not look like a raw key is passed through untouched:
 * custom-field names arrive already human, and lowercasing them would mangle
 * their capitalization. */
export function humanizeField(field: string): string {
  const looksRaw = /^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(field);
  if (!looksRaw) return field;
  const label = fieldLabel(field);
  return label ? capitalizeLabel(label) : field;
}

/** Capitalize a label for standalone use, leaving the rest of it untouched. */
export function capitalizeLabel(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}
